/**
 * Money primitives.
 *
 * RULE: no JavaScript floating point arithmetic is permitted anywhere in the
 * financial path. All amounts are carried as `Decimal` (decimal.js) in memory
 * and as PostgreSQL `numeric` on disk. Amounts cross the API boundary as
 * canonical decimal STRINGS ("1234567.89"), never as JS numbers, so that no
 * precision is lost in JSON.
 */
import Decimal from 'decimal.js';
// 28 significant digits is far beyond anything Indian real-estate accounting
// needs (largest realistic figure is ~1e12), and keeps intermediate rounding
// (e.g. percentage-of-value milestones) exact for our purposes.
Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_UP, toExpNeg: -18, toExpPos: 30 });
export { Decimal };
/** Scale used for all monetary columns (rupees + paise). */
export const MONEY_SCALE = 2;
/** Scale used for quantities (materials are issued in fractional units). */
export const QTY_SCALE = 4;
/** Scale used for rates/percentages. */
export const RATE_SCALE = 6;
/**
 * Coerce anything into a Decimal. Numbers are accepted only because Prisma and
 * JSON bodies can legitimately hand us one; they are converted via their string
 * form so `0.1 + 0.2` style artefacts never enter the ledger.
 */
export function toDecimal(value) {
    if (value === null || value === undefined || value === '')
        return new Decimal(0);
    if (value instanceof Decimal)
        return value;
    if (typeof value === 'number') {
        if (!Number.isFinite(value))
            throw new TypeError(`Non-finite amount: ${value}`);
        return new Decimal(value.toString());
    }
    const trimmed = String(value).trim().replace(/,/g, '');
    if (trimmed === '' || trimmed === '-')
        return new Decimal(0);
    const d = new Decimal(trimmed);
    if (!d.isFinite())
        throw new TypeError(`Non-finite amount: ${value}`);
    return d;
}
/** Round to money scale (2dp) using banker-free HALF_UP, as Indian tax law expects. */
export function money(value) {
    return toDecimal(value).toDecimalPlaces(MONEY_SCALE, Decimal.ROUND_HALF_UP);
}
export function qty(value) {
    return toDecimal(value).toDecimalPlaces(QTY_SCALE, Decimal.ROUND_HALF_UP);
}
export function rate(value) {
    return toDecimal(value).toDecimalPlaces(RATE_SCALE, Decimal.ROUND_HALF_UP);
}
/** Canonical wire format: fixed 2dp string. */
export function moneyString(value) {
    return money(value).toFixed(MONEY_SCALE);
}
export function qtyString(value) {
    return qty(value).toFixed(QTY_SCALE);
}
export function sum(values) {
    return values.reduce((acc, v) => acc.plus(toDecimal(v)), new Decimal(0));
}
export function sumMoney(values) {
    return money(sum(values));
}
export function isZero(value) {
    return money(value).isZero();
}
export function isNegative(value) {
    return money(value).isNegative();
}
export function eq(a, b) {
    return money(a).equals(money(b));
}
/** a - b at money scale. */
export function minus(a, b) {
    return money(toDecimal(a).minus(toDecimal(b)));
}
/** a + b at money scale. */
export function plus(a, b) {
    return money(toDecimal(a).plus(toDecimal(b)));
}
/** value * percent / 100, rounded to money scale. */
export function percentOf(value, percent) {
    return money(toDecimal(value).times(toDecimal(percent)).dividedBy(100));
}
/**
 * Extract the tax component from a tax-INCLUSIVE amount.
 * gross = net * (1 + r/100)  =>  tax = gross * r / (100 + r)
 */
export function taxFromInclusive(gross, percent) {
    const r = toDecimal(percent);
    if (r.isZero())
        return new Decimal(0);
    return money(toDecimal(gross).times(r).dividedBy(r.plus(100)));
}
/**
 * Split `total` across `weights` so the parts sum EXACTLY to `total`.
 * Largest-remainder method: no paisa is ever created or destroyed by rounding.
 * Used for apportioning tax / overheads across voucher lines, cost heads and
 * units, where a naive per-line round would leave a residue.
 */
export function allocate(total, weights) {
    const t = money(total);
    const w = weights.map((x) => toDecimal(x));
    const weightTotal = w.reduce((a, b) => a.plus(b), new Decimal(0));
    if (w.length === 0)
        return [];
    if (weightTotal.isZero()) {
        // Degenerate case: spread evenly, remainder onto the first part.
        const each = money(t.dividedBy(w.length));
        const parts = w.map(() => each);
        parts[0] = money(t.minus(each.times(w.length - 1)));
        return parts;
    }
    const raw = w.map((x) => t.times(x).dividedBy(weightTotal));
    const floored = raw.map((x) => x.toDecimalPlaces(MONEY_SCALE, Decimal.ROUND_DOWN));
    const distributed = floored.reduce((a, b) => a.plus(b), new Decimal(0));
    let residue = t.minus(distributed);
    // One paisa at a time to the largest fractional remainders.
    const step = new Decimal(1).dividedBy(new Decimal(10).pow(MONEY_SCALE));
    const order = raw
        .map((x, i) => ({ i, frac: x.minus(floored[i]) }))
        .sort((a, b) => b.frac.comparedTo(a.frac));
    let k = 0;
    while (residue.greaterThanOrEqualTo(step) && order.length > 0) {
        const idx = order[k % order.length].i;
        floored[idx] = floored[idx].plus(step);
        residue = residue.minus(step);
        k += 1;
    }
    return floored;
}
/** Indian digit grouping: 1,23,45,678.90 */
export function formatINR(value, opts = {}) {
    const decimals = opts.decimals ?? 2;
    const d = toDecimal(value).toDecimalPlaces(decimals, Decimal.ROUND_HALF_UP);
    const negative = d.isNegative();
    const abs = d.abs().toFixed(decimals);
    const [intPart, decPart] = abs.split('.');
    let grouped;
    if (intPart.length <= 3) {
        grouped = intPart;
    }
    else {
        const last3 = intPart.slice(-3);
        const rest = intPart.slice(0, -3);
        grouped = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
    }
    const body = decPart ? `${grouped}.${decPart}` : grouped;
    const symbol = opts.symbol === false ? '' : '₹';
    return `${negative ? '-' : ''}${symbol}${body}`;
}
/** Compact Indian display: ₹18.42 Cr / ₹4.20 L / ₹52,300 */
export function formatINRCompact(value) {
    const d = toDecimal(value);
    const abs = d.abs();
    const sign = d.isNegative() ? '-' : '';
    const CRORE = new Decimal(10000000);
    const LAKH = new Decimal(100000);
    if (abs.greaterThanOrEqualTo(CRORE)) {
        return `${sign}₹${abs.dividedBy(CRORE).toDecimalPlaces(2).toString()} Cr`;
    }
    if (abs.greaterThanOrEqualTo(LAKH)) {
        return `${sign}₹${abs.dividedBy(LAKH).toDecimalPlaces(2).toString()} L`;
    }
    return formatINR(d, { decimals: 0 });
}
/** Amount in words, Indian system — required on receipts and cheques. */
export function amountInWords(value) {
    const d = money(value);
    const negative = d.isNegative();
    const abs = d.abs();
    const rupees = abs.toDecimalPlaces(0, Decimal.ROUND_DOWN);
    const paise = abs.minus(rupees).times(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
    const words = `${numberToIndianWords(rupees)} Rupees${paise.isZero() ? '' : ` and ${numberToIndianWords(paise)} Paise`} Only`;
    return `${negative ? 'Minus ' : ''}${words}`;
}
const ONES = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
function twoDigits(n) {
    if (n < 20)
        return ONES[n];
    const t = Math.floor(n / 10);
    const o = n % 10;
    return `${TENS[t]}${o ? ` ${ONES[o]}` : ''}`;
}
function threeDigits(n) {
    const h = Math.floor(n / 100);
    const r = n % 100;
    const parts = [];
    if (h)
        parts.push(`${ONES[h]} Hundred`);
    if (r)
        parts.push(twoDigits(r));
    return parts.join(' ');
}
function numberToIndianWords(value) {
    let n = value.toDecimalPlaces(0, Decimal.ROUND_DOWN);
    if (n.isZero())
        return 'Zero';
    const chunks = [];
    const CRORE = new Decimal(10000000);
    const LAKH = new Decimal(100000);
    const THOUSAND = new Decimal(1000);
    const crores = n.dividedBy(CRORE).toDecimalPlaces(0, Decimal.ROUND_DOWN);
    if (crores.greaterThan(0)) {
        // Crores can themselves exceed 999 (e.g. 12,345 crore) — recurse.
        chunks.push(`${numberToIndianWords(crores)} Crore`);
        n = n.minus(crores.times(CRORE));
    }
    const lakhs = n.dividedBy(LAKH).toDecimalPlaces(0, Decimal.ROUND_DOWN).toNumber();
    if (lakhs > 0) {
        chunks.push(`${twoDigits(lakhs)} Lakh`);
        n = n.minus(new Decimal(lakhs).times(LAKH));
    }
    const thousands = n.dividedBy(THOUSAND).toDecimalPlaces(0, Decimal.ROUND_DOWN).toNumber();
    if (thousands > 0) {
        chunks.push(`${twoDigits(thousands)} Thousand`);
        n = n.minus(new Decimal(thousands).times(THOUSAND));
    }
    const rest = n.toNumber();
    if (rest > 0)
        chunks.push(threeDigits(rest));
    return chunks.join(' ').replace(/\s+/g, ' ').trim();
}
//# sourceMappingURL=money.js.map